<template>
  <!-- Floating chat button -->
  <button
    v-if="!isOpen && enabled"
    @click="open"
    class="fixed bottom-[5.5rem] right-4 z-40 w-14 h-14 rounded-full bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-purple-500/30 flex items-center justify-center hover:scale-110 transition-transform"
  >
    <span class="text-2xl">🤖</span>
    <div v-if="hasUnread" class="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse"></div>
  </button>

  <!-- Chat panel -->
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-4 scale-95"
    enter-to-class="opacity-100 translate-y-0 scale-100"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0 translate-y-4 scale-95"
  >
    <div
      v-if="isOpen && enabled"
      class="fixed bottom-0 right-0 sm:bottom-4 sm:right-4 z-50 w-full sm:w-[420px] h-[85vh] sm:h-[640px] bg-white dark:bg-gray-900 sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700"
    >
      <!-- Header -->
      <div class="bg-gradient-to-r from-violet-500 to-purple-600 p-4 flex items-center gap-3 flex-shrink-0">
        <div class="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-xl">🤖</div>
        <div class="flex-1">
          <h3 class="text-white font-bold">{{ t('gamification.chatbot.title') }}</h3>
          <p class="text-purple-200 text-xs">{{ t('gamification.chatbot.subtitle') }}</p>
        </div>
        <button @click="close" class="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white rounded-full hover:bg-white/10 transition-colors">✕</button>
      </div>

      <!-- Messages -->
      <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 space-y-3">
        <!-- Welcome message -->
        <div v-if="messages.length === 0" class="text-center py-6">
          <div class="text-5xl mb-3">👨‍🍳</div>
          <h4 class="text-gray-900 dark:text-white font-bold text-lg">{{ t('gamification.chatbot.welcome') }}</h4>
          <p class="text-gray-500 dark:text-gray-400 text-sm mt-1">
            {{ t('gamification.chatbot.welcomeMessage') }}
          </p>

          <!-- Dynamic quick actions -->
          <div class="mt-4 flex flex-wrap justify-center gap-2">
            <button
              v-for="q in promptSuggestions" :key="q"
              @click="sendQuick(q)"
              class="text-xs bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 px-3 py-1.5 rounded-full hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors border border-purple-200 dark:border-purple-700"
            >{{ q }}</button>
          </div>
        </div>

        <!-- Message list -->
        <div v-for="(msg, i) in messages" :key="i" :class="['flex', msg.role === 'user' ? 'justify-end' : 'justify-start']">
          <div :class="[
            'max-w-[85%] rounded-2xl px-4 py-2.5',
            msg.role === 'user'
              ? 'bg-purple-500 text-white rounded-br-md'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-bl-md'
          ]">
            <p class="text-sm whitespace-pre-wrap leading-relaxed">{{ msg.content }}</p>

            <!-- Product recommendations -->
            <div v-if="getRecommendedProducts(msg).length" class="mt-3 space-y-2">
              <p class="text-[11px] font-bold uppercase tracking-wider opacity-50 mb-1">Empfohlene Produkte</p>
              <div
                v-for="p in getRecommendedProducts(msg)" :key="p.id"
                class="product-card flex items-center gap-3 rounded-xl p-2.5"
              >
                <div class="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-gray-300 dark:bg-gray-600 shadow-sm">
                  <img
                    v-if="getProductImage(p)"
                    :src="getProductImage(p)"
                    :alt="p.name"
                    class="w-full h-full object-cover"
                    @error="$event.target.style.display='none'"
                  />
                  <div v-else class="w-full h-full flex items-center justify-center text-xl">
                    {{ p.is_beverage ? '🥤' : '🍽️' }}
                  </div>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-bold truncate">{{ p.name }}</p>
                  <p v-if="p.description" class="text-[11px] opacity-50 truncate mt-0.5">{{ p.description }}</p>
                  <p class="text-sm font-bold text-purple-500 dark:text-purple-400 mt-0.5">€{{ formatPrice(p.price) }}</p>
                </div>
                <button
                  @click="addProductToCart(p)"
                  :disabled="addedProducts.has(p.id)"
                  :class="[
                    'flex-shrink-0 text-xs px-4 py-2 rounded-xl font-bold whitespace-nowrap transition-all shadow-sm',
                    addedProducts.has(p.id)
                      ? 'bg-green-500 text-white cursor-default'
                      : 'bg-purple-500 hover:bg-purple-600 text-white active:scale-95'
                  ]"
                >{{ addedProducts.has(p.id) ? `✓ ${t('gamification.chatbot.inCart')}` : `+ ${t('gamification.chatbot.order')}` }}</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Typing indicator -->
        <div v-if="isTyping" class="flex justify-start">
          <div class="bg-gray-100 dark:bg-gray-800 rounded-2xl rounded-bl-md px-4 py-3">
            <div class="flex gap-1">
              <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
              <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
              <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Suggestion chips (after messages exist) -->
      <div v-if="messages.length > 0 && !isTyping" class="px-3 pb-1 flex-shrink-0">
        <div class="flex gap-1.5 overflow-x-auto no-scrollbar pb-1">
          <button
            v-for="s in followUpSuggestions" :key="s"
            @click="sendQuick(s)"
            class="text-[11px] bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2.5 py-1 rounded-full hover:bg-purple-100 dark:hover:bg-purple-900/30 hover:text-purple-600 dark:hover:text-purple-400 transition-colors whitespace-nowrap flex-shrink-0 border border-gray-200 dark:border-gray-700"
          >{{ s }}</button>
        </div>
      </div>

      <!-- Input -->
      <div class="p-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
        <form @submit.prevent="sendMessage" class="flex gap-2">
          <input
            ref="inputRef"
            v-model="inputText"
            :placeholder="t('gamification.chatbot.inputPlaceholder')"
            class="flex-1 px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            :disabled="isTyping"
          />
          <button
            type="submit"
            :disabled="!inputText.trim() || isTyping"
            class="w-10 h-10 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white flex items-center justify-center disabled:opacity-50 hover:scale-105 transition-transform"
          >↑</button>
        </form>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGamificationStore } from '@/stores/gamification'
import { useCartStore } from '@/stores/cart'
import { gamificationService } from '@/services/api/gamificationService'

const props = defineProps({
  orderSessionId: [Number, String],
  guestProfileId: [Number, String],
  enabled: { type: Boolean, default: true },
})

const { t } = useI18n()
const store = useGamificationStore()
const cartStore = useCartStore()

const isOpen = ref(false)
const inputText = ref('')
const hasUnread = ref(false)
const messagesContainer = ref(null)
const inputRef = ref(null)
const addedProducts = reactive(new Set())
const promptSuggestions = ref([
  'Was empfiehlst du heute?',
  'Ich bin vegan',
  'Welches Getränk passt?',
  'Etwas Leichtes bitte',
  'Was ist beliebt?',
])

const messages = computed(() => store.chatMessages)
const isTyping = computed(() => store.chatLoading)

const followUpSuggestions = computed(() => {
  if (messages.value.length === 0) return []
  const lastMsg = messages.value[messages.value.length - 1]
  if (lastMsg?.role !== 'assistant') return []
  const prods = getRecommendedProducts(lastMsg)
  const hasBeverage = prods.some(p => p.is_beverage)
  const suggestions = []
  if (prods.length > 0 && !hasBeverage) {
    suggestions.push('Welches Getränk passt dazu?')
  }
  suggestions.push('Gibt es Alternativen?')
  if (prods.length > 0) {
    suggestions.push(`Als Menü günstiger?`)
  }
  suggestions.push('Etwas als Beilage?', 'Noch ein Dessert?')
  return suggestions.slice(0, 4)
})

watch(messages, async () => {
  await nextTick()
  scrollToBottom()
}, { deep: true })

onMounted(async () => {
  try {
    const { data } = await gamificationService.getChatSuggestions()
    if (data?.suggestions?.length) {
      promptSuggestions.value = data.suggestions
    }
  } catch { /* use defaults */ }
})

function open() {
  isOpen.value = true
  hasUnread.value = false
  nextTick(() => inputRef.value?.focus())
}

function close() {
  isOpen.value = false
}

function scrollToBottom() {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

function getRecommendedProducts(msg) {
  if (!msg.metadata) return []
  return msg.metadata.recommendedProducts || msg.metadata.recommended_products || []
}

function getProductImage(product) {
  let url = product.imageUrl || product.image_url
  if (!url) return null
  url = String(url).trim()
  if (url.startsWith('/')) return url
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      return new URL(url).pathname || null
    } catch {
      return null
    }
  }
  const p = url.replace(/^\/?storage\/?/, '')
  return `/storage/${p}`
}

async function sendMessage() {
  const text = inputText.value.trim()
  if (!text || isTyping.value) return

  inputText.value = ''
  const sessionId = props.orderSessionId || parseInt(localStorage.getItem('sessionNumericId')) || null
  try {
    await store.sendMessage(sessionId, text, props.guestProfileId)
  } catch {
    // Error handled in store
  }
}

function sendQuick(q) {
  inputText.value = q
  sendMessage()
}

function formatPrice(price) {
  return parseFloat(price).toFixed(2)
}

async function addProductToCart(product) {
  if (addedProducts.has(product.id)) return
  try {
    const defaults = (product.default_extras || product.defaultExtras || []).map(ex => ({
      id: ex.id,
      name: ex.name,
      price: parseFloat(ex.price ?? ex.priceModifier ?? 0),
      priceModifier: parseFloat(ex.priceModifier ?? ex.price ?? 0),
      extraGroupId: ex.extraGroupId || ex.extra_group_id,
      extraGroupName: ex.extraGroupName || ex.extra_group_name || '',
    }))
    await cartStore.addItem(
      {
        id: product.id,
        name: product.name,
        price: parseFloat(product.price) || 0,
        imageUrl: product.imageUrl || product.image_url || '',
        description: product.description || '',
      },
      1, defaults, ''
    )
    addedProducts.add(product.id)
  } catch (e) {
    console.error('[AiChatbot] Fehler beim Hinzufügen:', e)
  }
}
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar { display: none; }
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
.product-card {
  background: rgba(124, 58, 237, 0.08);
  border: 1px solid rgba(124, 58, 237, 0.2);
  transition: all 0.2s ease;
}
.product-card:hover {
  background: rgba(124, 58, 237, 0.14);
  border-color: rgba(124, 58, 237, 0.35);
}
:root.dark .product-card,
.dark .product-card {
  background: rgba(124, 58, 237, 0.12);
  border-color: rgba(124, 58, 237, 0.25);
}
:root.dark .product-card:hover,
.dark .product-card:hover {
  background: rgba(124, 58, 237, 0.2);
}
</style>
